'use client';

import { Fragment, useId, useMemo, useState } from 'react';
import { ArrowRight, Check, Copy, RefreshCw } from 'lucide-react';
import { findUpdateCycle, writeInvestigationToText, investigationTextToHtml, type AccessGuardRow, type EventBurstStats, type EventStats, type WriteInvestigation } from '@/app/access-guard/logic';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { MAX_RESOURCE_EVENT_SAMPLES } from '@/lib/urm-api';

const dateFormat = new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
const timeFormat = new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
const preciseTimeFormat = new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3, hourCycle: 'h23' });
const actorColors = ['border-blue-300 bg-blue-50 text-blue-900', 'border-emerald-300 bg-emerald-50 text-emerald-900', 'border-amber-300 bg-amber-50 text-amber-900'];

function TimeRange({ firstTime, lastTime }: Pick<EventBurstStats, 'firstTime' | 'lastTime'>) {
  if (!firstTime || !lastTime) return <>Unavailable</>;
  const first = new Date(firstTime);
  const last = new Date(lastTime);
  if (firstTime === lastTime) return <><time dateTime={firstTime} title={firstTime}>{dateFormat.format(first)} {timeFormat.format(first)}</time> UTC</>;
  const format = last.getTime() - first.getTime() < 1000 ? preciseTimeFormat : timeFormat;
  return <>
    <time dateTime={firstTime} title={firstTime}>{dateFormat.format(first)} {format.format(first)}</time>
    {' – '}
    <time dateTime={lastTime} title={lastTime}>{firstTime.slice(0, 10) !== lastTime.slice(0, 10) && dateFormat.format(last) + ' '}{format.format(last)}</time> UTC
  </>;
}

function StatsCells({ stats }: { stats: EventStats }) {
  return <>
    <td className="px-3 py-3 text-right align-top tabular-nums">{stats.count.toLocaleString()}</td>
    <td className="px-3 py-3 align-top">
      <p className="text-right font-semibold tabular-nums">{stats.oneMinute.count.toLocaleString()}</p>
      <p className="mt-1 text-right text-xs text-muted-foreground"><TimeRange {...stats.oneMinute} /></p>
    </td>
    <td className="whitespace-nowrap px-3 py-3 text-right align-top text-base font-semibold tabular-nums">
      {stats.oneMinute.estimatedRps === null ? <span className="text-sm font-normal text-muted-foreground">Unavailable</span>
        : '≈' + stats.oneMinute.estimatedRps.toLocaleString(undefined, { maximumSignificantDigits: 3 })}
    </td>
    <td className="px-3 py-3 text-right align-top tabular-nums">
      {stats.activeBurst.estimatedEps === null ? <span className="text-sm text-muted-foreground">Unavailable</span> :
        <p className="whitespace-nowrap text-base font-semibold">≈{stats.activeBurst.estimatedEps.toLocaleString(undefined, { maximumSignificantDigits: 3 })}</p>
      }
      {stats.activeBurst.peak ? <div className="mt-1 text-xs text-muted-foreground">
        <p>{stats.activeBurst.peak.count.toLocaleString()} updates in peak burst</p>
        {stats.activeBurst.peak.count > 0 && <p><TimeRange {...stats.activeBurst.peak} /></p>}
      </div> : stats.activeBurst.estimatedEps !== null &&
        <p className="mt-1 whitespace-nowrap text-xs text-muted-foreground">{stats.activeBurst.burstCount.toLocaleString()} burst{stats.activeBurst.burstCount === 1 ? '' : 's'}</p>}
    </td>
  </>;
}

export function WriteEventBreakdown({ row, result, loading, samples, onSamplesChange, onRefresh, onCancel }: {
  row: AccessGuardRow;
  result?: WriteInvestigation;
  loading: boolean;
  samples: string;
  onSamplesChange: (value: string) => void;
  onRefresh: () => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const validSamples = Number.isInteger(Number(samples)) && Number(samples) >= 1 && Number(samples) <= MAX_RESOURCE_EVENT_SAMPLES;
  const copyInvestigation = async () => {
    if (!result || result.error || loading) return;
    try {
      const text = writeInvestigationToText(row, result);
      if (typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function') {
        try {
          await navigator.clipboard.write([new ClipboardItem({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([investigationTextToHtml(text)], { type: 'text/html' }),
          })]);
        } catch {
          await navigator.clipboard.writeText(text);
        }
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  };
  const updates = result?.users.reduce((total, user) => total + user.count, 0) ?? 0;
  const sequence = result?.sequence ?? [];
  const cycle = useMemo(() => findUpdateCycle(result?.sequence ?? []), [result?.sequence]);
  const visibleSteps = cycle?.steps.slice(-12) ?? [];
  const excludedTimes = updates - sequence.reduce((total, step) => total + step.count, 0);
  const actorIndices = new Map((result?.users ?? []).map((user, index) => [user.user, index] as const));
  const actorIndex = (user: string) => actorIndices.get(user) ?? 0;
  const actorBadge = (user: string) => {
    const index = actorIndex(user);
    return <Badge variant="outline" className={cn('shrink-0 whitespace-nowrap', actorColors[index % actorColors.length])}>User {index + 1}</Badge>;
  };

  return <section aria-label="Related Events Activity" aria-busy={loading} className="space-y-4 rounded-lg border bg-background p-4 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Related Events Activity</h3>
        {result && !result.error && <Badge variant="secondary">{result.count.toLocaleString()} samples</Badge>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={id + '-samples'} className="text-xs font-medium text-muted-foreground">Samples</label>
        <input id={id + '-samples'} type="number" min={1} max={MAX_RESOURCE_EVENT_SAMPLES} step={1} value={samples}
          disabled={loading} aria-invalid={!validSamples} aria-describedby={!validSamples ? id + '-samples-error' : undefined}
          onChange={(event) => { setCopyStatus('idle'); onSamplesChange(event.target.value); }}
          className="h-8 w-24 rounded-md border bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <Button variant="outline" size="sm" disabled={loading || !validSamples} aria-label="Refresh resource events"
          onClick={() => { setCopyStatus('idle'); onRefresh(); }}>
          <RefreshCw aria-hidden="true" className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {loading ? 'Loading…' : result?.error ? 'Retry' : 'Refresh'}
        </Button>
        <Button variant="outline" size="sm" disabled={loading || !result || !!result.error}
          onClick={() => void copyInvestigation()} aria-label="Copy investigation" aria-live="polite">
          {copyStatus === 'copied' ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copyStatus === 'copied' ? 'Copied' : 'Copy'}
        </Button>
        {loading && <Button variant="ghost" size="sm" onClick={onCancel}>Cancel investigation</Button>}
      </div>
    </div>
    {!validSamples && <p id={id + '-samples-error'} role="alert" className="text-xs text-destructive">Enter a whole number from 1 to {MAX_RESOURCE_EVENT_SAMPLES.toLocaleString()} samples.</p>}
    {copyStatus === 'error' && <p role="alert" className="text-sm text-destructive">Could not copy investigation to the clipboard. Try again.</p>}
    {loading && <p role="status" className="text-sm text-muted-foreground">Loading resource events…</p>}
    {!loading && result?.error && <p role="alert" className="text-sm text-destructive">Could not investigate resource: {result.error}</p>}
    {!loading && !result && <p className="text-sm text-muted-foreground">Refresh to load resource events.</p>}
    {result && !result.error && <>
      {!result.users.length && <p className="py-3 text-sm text-muted-foreground">No update events found for this resource.</p>}
      {/* Keep the sequence out of the parent table's intrinsic width calculation. */}
      {cycle && <div className="min-w-0 rounded-md border bg-muted/20 p-3 [contain:inline-size]">
        <h4 className="text-sm font-medium">Suspected update cycle</h4>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {cycle.users.map((user, index) => <Fragment key={index}>
            {index > 0 && <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
            <a href={'#' + id + '-user-' + actorIndex(user)} aria-label={'Show user ' + user}>{actorBadge(user)}</a>
          </Fragment>)}
          <span>· {cycle.repetitions.toLocaleString()} repetitions</span>
          {cycle.typicalRoundTripSeconds !== null && <span>· Typical round-trip: {cycle.typicalRoundTripSeconds.toLocaleString(undefined, { maximumSignificantDigits: 3 })} s</span>}
        </div>
        {cycle.sharedFields.length > 0 && <p className="mt-2 text-xs [overflow-wrap:anywhere]">Shared fields: {cycle.sharedFields.join(', ')}</p>}
        <p className="mt-2 text-xs text-muted-foreground"><TimeRange firstTime={cycle.steps[0].firstTime} lastTime={cycle.steps.at(-1)!.lastTime} /> · Latest {visibleSteps.length} of {cycle.steps.length} cycle steps</p>
        <ol className="mt-3 flex max-w-full items-start gap-2 overflow-x-auto pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" tabIndex={0} aria-label="Chronological update sequence">
          {visibleSteps.map((step, index) => <li key={index} className="flex shrink-0 items-center gap-2">
            <div className="w-64 space-y-2 rounded-md border bg-background p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <a href={'#' + id + '-user-' + actorIndex(step.user)} aria-label={'Show user ' + step.user}>{actorBadge(step.user)}</a>
                <span className="text-muted-foreground">{step.count.toLocaleString()} update{step.count === 1 ? '' : 's'}</span>
              </div>
              <p className="text-muted-foreground"><TimeRange {...step} /></p>
              <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{step.message}</p>
            </div>
            {index < visibleSteps.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
          </li>)}
        </ol>
        <p className="mt-2 text-xs text-muted-foreground">At least three repetitions, with no gaps over one minute. This suggests a cycle; it does not establish causation. Equal timestamps have uncertain order.</p>
      </div>}
      {cycle && excludedTimes > 0 && <p className="text-xs text-muted-foreground">{excludedTimes.toLocaleString()} updates without valid timestamps are excluded from the sequence.</p>}
      {result.users.map((user, index) => <div key={user.user} id={id + '-user-' + index} className="space-y-2 border-t pt-3">
        <div className="flex items-start gap-2">{actorBadge(user.user)}<p className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">{user.user}</p></div>
        {user.preBlock ? <div className="text-xs text-muted-foreground">
          <p>WRITE blocked: <TimeRange firstTime={user.preBlock.blockedAt} lastTime={user.preBlock.blockedAt} /></p>
          <p>Rates use the 10 minutes before this block. Active burst pace uses the busiest sliding minute.</p>
          {!user.preBlock.complete && <p className="text-amber-700 dark:text-amber-400">The sample does not cover the full pre-block window; rates are unavailable. Increase Samples and refresh.</p>}
        </div> : <p className="text-xs text-muted-foreground">No WRITE-block event found for this user. Rates use the fetched sample.</p>}
        {!user.messages.length && <p className="text-sm text-muted-foreground">No included update events found for this user.</p>}
        <div className="min-w-0 overflow-x-auto rounded-md border [contain:inline-size] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" tabIndex={0} role="region" aria-label={'Messages for user ' + user.user}>
          <table className="w-full min-w-[720px] text-left text-sm">
            <caption className="sr-only">Update messages for user {user.user}</caption>
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr><th scope="col" className="w-1/2 px-3 py-2 font-medium">Message</th><th scope="col" className="px-3 py-2 text-right font-medium">Updates</th><th scope="col" className="px-3 py-2 text-right font-medium">Busiest 1 min</th><th scope="col" className="px-3 py-2 text-right font-medium">Minute rate<br /><span className="font-normal">Est. requests/s</span></th><th scope="col" className="px-3 py-2 text-right font-medium">Active burst pace<br /><span className="font-normal">Events/s</span></th></tr>
            </thead>
            <tbody className="divide-y">
              {user.messages.length > 1 && <tr className="bg-muted/20"><th scope="row" className="px-3 py-3 align-top font-medium">All messages</th><StatsCells stats={user} /></tr>}
              {user.messages.map((message) => <tr key={message.message}><th scope="row" className="whitespace-pre-wrap px-3 py-3 align-top font-normal [overflow-wrap:anywhere]">{message.message}</th><StatsCells stats={message} /></tr>)}
            </tbody>
          </table>
        </div>
      </div>)}
      {result.users.length > 0 && <p className="text-xs text-muted-foreground">Update events are included except TriggerWebhookCall and WRITE-block markers. Counts cover the fetched sample. Minute rate = update count in the busiest sliding minute ÷ first-to-last elapsed seconds. Pre-block burst pace = 1 ÷ median positive gap in the busiest minute. Without a block marker, sample burst pace uses resource bursts split at gaps over 1 second. Samples controls the initial scan; detected block windows are fetched automatically. Events may not correspond one-to-one with requests.</p>}
    </>}
  </section>;
}
